import {
  isSkippedAnswer,
  normalizeAnswerValue,
} from "../CoachAssessment/QuestionField";

export { isSkippedAnswer };

export const GATE_GLP1 = "diet_glp1";
export const GATE_COMORBID = "diet_comorbidities";
export const GATE_COMORBID_IDS = ["diet_comorbidities", "diet_comorbid"];
export const FIELD_HEALTH_GOAL = "diet_health_goal";
export const FIELD_MEDS = "diet_cur_meds";
export const FIELD_ALLERGIES = "diet_allergies";

export type ShowIfClause = {
  questionId?: string;
  equals?: unknown;
  notEquals?: unknown;
  includes?: string;
  in?: unknown[];
  any?: ShowIfClause[];
};

export type ShowIfRule = ShowIfClause | null | undefined;

export const isSharedProfileSection = (section: { sharedProfile?: boolean }) =>
  section?.sharedProfile === true;

export const assessmentQuestions = (config: any): any[] => {
  const sharedIds = new Set(
    (config?.sections || [])
      .filter((section: any) => isSharedProfileSection(section))
      .map((section: any) => section.id),
  );
  if (!sharedIds.size) return config?.questions || [];
  return (config?.questions || []).filter(
    (question: any) => !sharedIds.has(question.sectionId),
  );
};

export const isAnswered = (val: unknown): boolean => {
  if (isSkippedAnswer(val)) return false;
  const value = normalizeAnswerValue(val);
  if (value == null) return false;
  if (typeof value === "string") return value.trim() !== "";
  if (typeof value === "number") return true;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") {
    return Object.values(value as Record<string, unknown>).some((item) =>
      isAnswered(item),
    );
  }
  return Boolean(value);
};

/**
 * Section was skipped by the patient when answers include skip markers and
 * no real answers remain in that section.
 */
export const isSectionSkipped = (
  questions: { id?: string }[],
  answers: Record<string, unknown>,
  _opts?: {
    sectionSkippable?: boolean;
    sessionDone?: boolean;
    patientFill?: boolean;
  },
): boolean => {
  const qs = questions.filter((q) => q?.id != null);
  if (!qs.length) return false;
  const anySkipped = qs.some((q) => isSkippedAnswer(answers[String(q.id)]));
  if (!anySkipped) return false;
  return qs.every((q) => {
    const raw = answers[String(q.id)];
    return raw == null || isSkippedAnswer(raw);
  });
};

const answerList = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.map(String).filter((item) => item !== "");
  if (value == null || value === "") return [];
  return [String(value)];
};

/** Toggle `yes`/`no` also matches boolean / "true" / "false" from other clients. */
const valuesMatch = (list: string[], expected: unknown): boolean => {
  const exp = String(expected).toLowerCase();
  const aliases =
    exp === "yes" ? ["yes", "true"] : exp === "no" ? ["no", "false"] : [exp];
  return list.some((item) => aliases.includes(item));
};

const matchClause = (
  clause: ShowIfClause,
  answers: Record<string, unknown>,
): boolean => {
  if (Array.isArray(clause.any) && clause.any.length) {
    return clause.any.some((item) => matchClause(item, answers));
  }
  const questionId = clause.questionId;
  if (!questionId) return true;
  const value = normalizeAnswerValue(answers[questionId]);
  const list = answerList(value).map((item) => item.toLowerCase());
  if (clause.equals != null) {
    return valuesMatch(list, clause.equals);
  }
  if (clause.notEquals != null) {
    return list.length > 0 && !valuesMatch(list, clause.notEquals);
  }
  if (clause.includes != null) {
    return valuesMatch(list, clause.includes);
  }
  if (Array.isArray(clause.in) && clause.in.length) {
    const allowed = clause.in.map((item) => String(item).toLowerCase());
    return list.some((item) => allowed.includes(item));
  }
  return list.length > 0;
};

export const sectionIsVisible = (
  section: { showIf?: ShowIfRule },
  answers: Record<string, unknown>,
): boolean => {
  if (!section?.showIf) return true;
  return matchClause(section.showIf, answers);
};

export const selectedComorbidityIds = (
  answers: Record<string, unknown>,
): string[] => {
  for (const gateId of GATE_COMORBID_IDS) {
    const value = normalizeAnswerValue(answers[gateId]);
    const list = Array.isArray(value)
      ? value.map(String)
      : value != null && value !== ""
        ? [String(value)]
        : [];
    if (list.length) return list;
  }
  return [];
};

/** Comorbidity follow-ups and `showIf` clauses; used by Shared Profile today. */
export const questionIsVisible = (
  question: { showIf?: ShowIfRule; includesComorbidities?: string[] },
  answers: Record<string, unknown>,
): boolean => {
  const comorbidityIds = question.includesComorbidities;
  if (Array.isArray(comorbidityIds) && comorbidityIds.length) {
    const selected = selectedComorbidityIds(answers);
    if (!comorbidityIds.some((id) => selected.includes(String(id)))) {
      return false;
    }
  }
  return sectionIsVisible(question, answers);
};

export const computeBmi = (weight: unknown, heightCm: unknown): number | null => {
  const kg = Number(weight);
  const cm = Number(heightCm);
  if (!Number.isFinite(kg) || !Number.isFinite(cm) || kg <= 0 || cm <= 0) {
    return null;
  }
  return Math.round((kg / (cm / 100) ** 2) * 10) / 10;
};
